import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Sparkles, ArrowRight, MapPin } from 'lucide-react';
import BrandLogo from './BrandLogo';

/**
 * Premium global site header — used across every page.
 * Logo on left (h-16 md:h-20 lg:h-24), nav in middle, Enroll CTA on right.
 *
 * Hash links (#about, #courses, #workshops, #testimonials, #faq) always
 * point to the HOME page (`/#section`) — when user clicks them from a
 * deep page, react-router takes them home then native browser scrolls.
 */
const NAV = [
  { label: 'Home',        href: '/' },
  { label: 'About',       href: '/#about' },
  { label: 'Programs',    href: '/#courses' },
  { label: 'Workshops',   href: '/#workshops' },
  { label: 'Testimonials',href: '/#testimonials' },
  { label: 'FAQ',         href: '/#faq' },
];

const SiteHeader = ({ ctaLabel = 'Enroll Now', ctaTo = '/book-demo' }) => {
  const [open, setOpen]   = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // When user navigates with hash from another page, smooth-scroll to section after route mount.
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      // wait for the page to paint
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
    }
  }, [location]);

  const onNavClick = (e, href) => {
    setOpen(false);
    // Same-page hash → smooth-scroll instead of full route change
    if (href.startsWith('/#') && (location.pathname === '/' || location.pathname === '')) {
      e.preventDefault();
      const id = href.split('#')[1];
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/' && !location.hash;
    if (href.startsWith('/#')) return location.pathname === '/' && location.hash === href.slice(1);
    return false;
  };

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 bg-white ${scrolled ? 'shadow-md border-b border-slate-200' : 'border-b border-slate-100'} transition-all`}
    >
      <div className="container mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex items-center justify-between gap-4 py-2 overflow-visible">

          {/* LEFT — premium logo block (20-25% width on desktop) */}
          <div className="flex-shrink-0 lg:min-w-[260px] -my-3 md:-my-4">
            <BrandLogo size="xl" className="px-1 md:px-2" />
          </div>

          {/* MIDDLE — desktop nav menu */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => onNavClick(e, item.href)}
                className={`text-sm font-semibold transition-colors ${isActive(item.href) ? 'text-orange-600' : 'text-slate-700 hover:text-orange-600'}`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                {item.label}
              </a>
            ))}
            <Link
              to="/noida"
              className="inline-flex items-center gap-1.5 text-sm font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-full px-3.5 py-1.5 transition-all"
              data-testid="nav-experience-centre"
            >
              <MapPin className="w-3.5 h-3.5" /> AI Experience Centre
              <span className="ml-1 text-[10px] font-black bg-emerald-600 text-white rounded-full px-1.5 py-0.5 leading-none">NEW</span>
            </Link>
            <Link
              to="/ai-bootcamp"
              className="inline-flex items-center gap-1.5 text-sm font-bold text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-full px-3.5 py-1.5 transition-all"
              data-testid="nav-summer-bootcamp"
            >
              <Sparkles className="w-3.5 h-3.5" /> Summer AI Bootcamp
            </Link>
          </nav>

          {/* RIGHT — primary CTA */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Link
              to={ctaTo}
              className="hidden sm:inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-full px-5 md:px-6 py-2.5 text-sm md:text-[15px] shadow-md hover:shadow-lg transition-all"
              data-testid="header-cta"
            >
              {ctaLabel} <ArrowRight className="w-4 h-4" />
            </Link>

            {/* Mobile menu trigger */}
            <button
              type="button"
              aria-label="Toggle menu"
              onClick={() => setOpen(!open)}
              className="lg:hidden p-2 -mr-1 rounded-lg hover:bg-slate-100"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="w-6 h-6 text-slate-900" /> : <Menu className="w-6 h-6 text-slate-900" />}
            </button>
          </div>
        </div>

        {/* MOBILE drawer */}
        {open && (
          <div className="lg:hidden pb-4 border-t border-slate-100 pt-3" data-testid="mobile-menu">
            <nav className="flex flex-col gap-1">
              {NAV.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={(e) => onNavClick(e, item.href)}
                  className="px-3 py-3 text-base font-semibold text-slate-800 hover:text-orange-600 hover:bg-orange-50 rounded-lg"
                >
                  {item.label}
                </a>
              ))}
              <Link
                to="/noida"
                onClick={() => setOpen(false)}
                className="px-3 py-3 text-base font-bold text-emerald-700 bg-emerald-50 rounded-lg inline-flex items-center gap-2"
              >
                <MapPin className="w-4 h-4" /> AI Experience Centre
                <span className="ml-1 text-[10px] font-black bg-emerald-600 text-white rounded-full px-1.5 py-0.5">NEW</span>
              </Link>
              <Link
                to="/ai-bootcamp"
                onClick={() => setOpen(false)}
                className="px-3 py-3 text-base font-bold text-orange-600 bg-orange-50 rounded-lg inline-flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" /> Summer AI Bootcamp
              </Link>
              <Link
                to={ctaTo}
                onClick={() => setOpen(false)}
                className="mt-3 inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-xl px-5 py-3 text-base"
              >
                {ctaLabel} <ArrowRight className="w-5 h-5" />
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default SiteHeader;
